import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { GlobalFilterProvider, useGlobalFilter } from './contexts/GlobalFilterContext';
import { useResponsive } from './hooks/useResponsive';
import LoginPage from './pages/auth/LoginPage';
import Sidebar from './components/layout/Sidebar';
import TopBar from './components/layout/TopBar';
import GlobalSearch from './components/common/GlobalSearch';
import NotificationCenter, { NotificationToast } from './components/common/NotificationCenter';
import ErrorBoundary from './components/common/ErrorBoundary';
import { TopProgressBar } from './components/common/TopProgressBar';
import { PageLoader } from './components/common/PageLoader';
import api from './services/api';
import { setCached } from './services/clientCache';
import { hydrateApiCache } from './services/apiCache';
import './App.css';

/**
 * ─── PERFORMANCE: Lazy-load every page ───
 *
 * Previously every Page component was a top-of-file import, which forced
 * webpack to bundle the entire app (Inventory, Reports, Halley, Recharts,
 * jspdf, html2canvas, xlsx, three.js, etc.) into a single 2.4 MB main.js
 * file. The user paid that download cost on first paint of the **Login**
 * page, before they'd even authenticated.
 *
 * Switching every page to React.lazy() lets webpack emit one chunk per
 * route. The browser only downloads the page the user actually visits,
 * with the rest fetched on demand. Expected effect: main.js shrinks
 * from ~2.4 MB to ~400 KB; per-page chunks land in the 30-300 KB range.
 *
 * Heavy export libraries (xlsx, jspdf, html2canvas) used inside
 * HalleyAdvanced were a ~2 MB drag on the AIHomePage chunk because they
 * were eagerly imported. Same story for Recharts on every dashboard.
 * Each of those pages now belongs to its own chunk and won't bloat any
 * sibling page.
 */
const AIHomePage         = lazy(() => import('./pages/home/AIHomePage'));
const DashboardPage      = lazy(() => import('./pages/dashboard/DashboardPage'));
const InventoryPage      = lazy(() => import('./pages/inventory/InventoryPage'));
const InboundPage        = lazy(() => import('./pages/inbound/InboundPage'));
const OutboundPage       = lazy(() => import('./pages/outbound/OutboundPage'));
const TransportPage      = lazy(() => import('./pages/transport/TransportPage'));
const DocumentsPage      = lazy(() => import('./pages/documents/DocumentsPage'));
const AppointmentsPage   = lazy(() => import('./pages/appointments/AppointmentsPage'));
const ExpiryPage         = lazy(() => import('./pages/expiry/ExpiryPage'));
const AlarmsPage         = lazy(() => import('./pages/alarms/AlarmsPage'));
const AlertRulesPage     = lazy(() => import('./pages/alarms/AlertRulesPage'));
const ReportsPage        = lazy(() => import('./pages/reports/ReportsPage'));
const SettingsPage       = lazy(() => import('./pages/settings/SettingsPage'));
const BookingsPage       = lazy(() => import('./pages/bookings/BookingsPage'));
const SatisfactionPage   = lazy(() => import('./pages/satisfaction/SatisfactionPage'));
const ProjectLeadersPage = lazy(() => import('./pages/settings/ProjectLeadersPage'));
const TeamsPage          = lazy(() => import('./pages/settings/TeamsPage'));
const ChatPage           = lazy(() => import('./pages/chat/ChatPage'));
const AnnouncementsPage  = lazy(() => import('./pages/announcements/AnnouncementsPage'));
const ApprovalsPage      = lazy(() => import('./pages/settings/ApprovalsPage'));
const CustomerScopePage  = lazy(() => import('./pages/settings/CustomerScopePage'));
const UnmappedBMFirmsPage = lazy(() => import('./pages/settings/UnmappedBMFirmsPage'));

const PAGE_TITLES: Record<string, string> = {
  'ai-home': 'Halley AI',
  dashboard: 'Dashboard',
  inventory: 'Inventory',
  inbound: 'Inbound',
  outbound: 'Outbound',
  transport: 'Transport',
  bookings: 'Bookings',
  documents: 'Documents',
  appointments: 'Appointments',
  expiry: 'Expiry Tracker',
  alarms: 'Alarms',
  'alert-rules': 'Alert Rules',
  reports: 'Reports',
  satisfaction: 'Satisfaction',
  'project-leaders': 'Project Leaders',
  chat: 'Team Chat',
  announcements: 'Announcements',
  approvals: 'Approvals',
  'customer-scope': 'Customer Scope',
  'unmapped-bmfirms': 'Unmapped BMFIRMs',
  settings: 'Settings',
};

/**
 * Skeleton fallback used while a lazy chunk is in flight. Renders a
 * full dashboard-shaped skeleton (so lazy navigation feels identical
 * to in-page loads) AND drives the global TopProgressBar via the
 * mount/unmount lifecycle of <PageLoader/>.
 */
const PageSuspenseFallback: React.FC = () => <PageLoader />;

const ProtectedLayout: React.FC = () => {
  const { isAuthenticated, loading, isCustomer, user } = useAuth();
  const gf = useGlobalFilter();
  const [activeMenu, setActiveMenu] = useState('ai-home');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { isMobile, isTablet } = useResponsive();

  // Global Ctrl+K / Cmd+K shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ── Idle prefetch of ALL main page chunks ──
  // Once the user has the home page rendered we use requestIdleCallback
  // to pull in the JS chunks for every page they can reach from the
  // sidebar. This warms the browser cache so the actual click-to-render
  // is near-instant (no <PageSuspenseFallback/> skeleton from a chunk
  // download) without delaying the initial home-page paint.
  //
  // FIX (Jun 01 2026): The client objected that "each page is loading" —
  // a spinner on every first navigation. Previously we only warmed 4
  // chunks (Dashboard / Inventory / Inbound / Outbound); every other
  // page still paid the chunk-download cost on first click. We now warm
  // ALL pages, in two waves so we don't fire a 20-request burst at once:
  //   wave 1 = the statistically most-visited operational pages,
  //   wave 2 = the rest (settings / admin / content), 800ms later.
  // Chunk prefetch is pure JS download — no API call, no role/scope
  // concern — so it is safe to warm every chunk regardless of who is
  // logged in. webpack de-dupes, so a later real click downloads nothing.
  useEffect(() => {
    if (!isAuthenticated) return;
    const w: any = window;
    let wave2Timer: ReturnType<typeof setTimeout> | undefined;

    const wave1 = () => {
      void import('./pages/dashboard/DashboardPage');
      void import('./pages/inventory/InventoryPage');
      void import('./pages/inbound/InboundPage');
      void import('./pages/outbound/OutboundPage');
      void import('./pages/transport/TransportPage');
      void import('./pages/reports/ReportsPage');
      void import('./pages/expiry/ExpiryPage');
      void import('./pages/alarms/AlarmsPage');
      // Schedule the remaining (less-visited) pages shortly after.
      wave2Timer = setTimeout(wave2, 800);
    };

    const wave2 = () => {
      void import('./pages/alarms/AlertRulesPage');
      void import('./pages/bookings/BookingsPage');
      void import('./pages/documents/DocumentsPage');
      void import('./pages/appointments/AppointmentsPage');
      void import('./pages/satisfaction/SatisfactionPage');
      void import('./pages/chat/ChatPage');
      void import('./pages/announcements/AnnouncementsPage');
      void import('./pages/settings/TeamsPage');
      void import('./pages/settings/SettingsPage');
      void import('./pages/settings/ProjectLeadersPage');
      void import('./pages/settings/ApprovalsPage');
      void import('./pages/settings/CustomerScopePage');
      void import('./pages/settings/UnmappedBMFirmsPage');
    };

    if (typeof w.requestIdleCallback === 'function') {
      const handle = w.requestIdleCallback(wave1, { timeout: 4000 });
      return () => {
        w.cancelIdleCallback?.(handle);
        if (wave2Timer) clearTimeout(wave2Timer);
      };
    }
    const t = setTimeout(wave1, 2500);
    return () => {
      clearTimeout(t);
      if (wave2Timer) clearTimeout(wave2Timer);
    };
  }, [isAuthenticated]);

  // ── Silent API warm-up after login (May 17, 2026) ──
  // Loading the chunks above gives us a fast paint, but the user still
  // pays the API latency on first click of each module. Here we ALSO
  // fire the heavy GETs in the background and seed clientCache with
  // the responses, so when the user clicks Inventory / Inbound / etc.
  // the page reads from cache and renders instantly (then refetches
  // silently in the background for freshness — that's how each page's
  // stale-while-revalidate effect already works).
  //
  // Implementation notes:
  // - We sequence the fetches with await + a small gap between groups
  //   so the browser can paint the home page first. Total budget
  //   ~6 seconds of background work.
  // - Each fetch is wrapped in try/catch so a single failure (e.g. a
  //   404 on an admin-only endpoint when the user is a customer) does
  //   not derail the rest of the warm-up.
  // - Cache scope keys MUST match what each page expects (`inventory:state`,
  //   `inbound:state`, etc.) — verified against the pages' own getCached
  //   call sites. If a page uses a different key, it just refetches
  //   normally — no regression risk.
  // - Cancel flag prevents writing into the cache after logout.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    // Load the chamber → storage-type map from Azure (StorageTypeMap) once,
    // so resolveStorageType() categorizes pallets from the operator-maintained
    // source of truth rather than the built-in fallback. Best-effort & cached.
    import('./utils/storageTypes')
      .then(m => m.loadStorageTypeOverrides())
      .catch(() => { /* keep built-in fallback */ });

    // Compute the SAME default-range date strings the heavy pages derive
    // from their default `dateRange='7d'` (InboundPage.tsx:242-249,
    // OutboundPage.tsx:261-268), so the warm-up hits the EXACT SWR cache
    // keys those pages will request. ISO YYYY-MM-DD → identical key for
    // any request made on the same calendar day.
    const _now = new Date();
    const _isoOf = (d: Date) => d.toISOString().slice(0, 10);
    const dateFrom7d = _isoOf(new Date(_now.getTime() - 6 * 86400000));
    const dateTo = _isoOf(_now);

    const warmUp = async () => {
      // ── warmRaw: fire the EXACT endpoint calls a page makes, purely to
      // prime the transparent SWR response cache (apiCache.ts: L1 RAM +
      // IndexedDB + in-flight dedup). We deliberately DON'T touch
      // clientCache here.
      //
      // FIX (Jun 01 2026): the old warm-up wrote RAW API payloads under
      // each page's `*:state` clientCache key, but every heavy page reads
      // a RENDER-SHAPED object from that key (cached.inventoryData,
      // cached.grRecords, cached.palletsDaily, …). The shapes never
      // matched, so getCached() returned an object full of `undefined`s —
      // the entire warm-up was silently DEAD for Inventory/Inbound/
      // Outbound/Transport/Expiry, and worse, it overwrote the correct
      // shape the page writes for itself. That's the real reason "every
      // page shows a spinner on first visit".
      //
      // Each heavy page already writes its OWN correctly-shaped `*:state`
      // cache after a successful fetch (InventoryPage:532, InboundPage:502,
      // OutboundPage:462, TransportPage:167, ExpiryPage:194). So the warm-
      // up's only job is to make that first fetch network-free: prime the
      // SWR cache for the exact GETs the page issues. The page then maps +
      // caches the shape itself — single source of truth, zero drift.
      const warmRaw = async (...fetchers: Array<() => Promise<any>>) => {
        if (cancelled) return;
        try {
          await Promise.allSettled(fetchers.map(f => f()));
        } catch {
          /* silent — page will refetch on click if it needs to */
        }
      };
      // warmShaped: for pages whose first-paint cache value EQUALS the raw
      // payload we produce here (an array or a composite we assemble), keep
      // seeding clientCache directly — those shapes DO match the reader.
      const warmShaped = async <T,>(
        scope: string,
        fetcher: () => Promise<T>,
        params?: Record<string, any>,
      ) => {
        if (cancelled) return;
        try {
          const data = await fetcher();
          if (!cancelled && data) {
            setCached(scope, params, data);
          }
        } catch {
          /* silent — page will refetch on click if it needs to */
        }
      };

      // High-priority — most-clicked heavy pages. Warm the EXACT calls.
      // Inventory (InventoryPage.tsx:409-414):
      await warmRaw(
        () => api.getInventory(),
        () => api.getDataStats(),
        () => api.getStockMovement(),
        () => api.getInventoryUOMGraph(),
        () => api.getInventoryABC(),
      );
      // Inbound bundle + secondaries (InboundPage.tsx:261-267). Staff-only
      // endpoints (truck-types / process-status) 403 for customers, so we
      // skip them for customer logins — same gate the page uses.
      await warmRaw(
        () => api.getInboundBundle({ limit: 500, date_from: dateFrom7d, date_to: dateTo }),
        ...(isCustomer ? [] : [
          () => api.getInboundTruckTypes(),
          () => api.getInboundProcessStatus(),
        ]),
        () => api.getInboundPipelineDetail(),
        () => api.getInboundTruckTimings(),
        () => api.getInboundTruckDurations(),
        () => api.getAlertInbound(),
      );
      // Small breather so the main thread can paint the home page.
      await new Promise(r => setTimeout(r, 250));

      // Outbound bundle (OutboundPage.tsx:273) — same default 7d range.
      await warmRaw(
        () => api.getOutboundBundle({ date_from: dateFrom7d, date_to: dateTo, limit: 200 }),
      );
      // Transport (TransportPage.tsx:105-108).
      await warmRaw(
        () => api.getBookings(),
        () => api.getTransportStats(),
        () => api.getTransportLiveStats(),
        () => api.getTransportDocks(),
      );
      await new Promise(r => setTimeout(r, 250));

      // Expiry — match the page's first fetch exactly so the SWR key lines
      // up. FIX (Jun 01 2026): the page now fetches just 50 detail rows by
      // default (KPIs/charts come from server aggregates), so warm the same
      // 50 instead of the old 10,000 — standing rule: never bring 10K rows.
      await warmRaw(
        () => api.getExpiryData(
          50,
          isCustomer ? (user?.customer_name || undefined) : undefined,
        ),
      );
      // Bookings / Appointments — light list pages that read the RAW
      // payload straight from clientCache, so seed the shaped key too
      // (preserves prior behaviour; the fetcher also primes SWR).
      await warmShaped('bookings:list',    () => api.getBookings());
      await warmShaped('appointments:list',() => api.getAppointments());
      await new Promise(r => setTimeout(r, 250));

      // Teams page (FIX Jun 01 2026): TeamsPage reads
      // getCached('teams:list', { manager: isManager }) and stores just
      // the `.teams` array. Warm it with the SAME key + the same unwrap
      // so the page paints instantly instead of showing a spinner.
      // isManager mirrors TeamsPage: manager OR admin. Customers never
      // reach this page; api.listTeams is scope-safe server-side.
      if (user?.role !== 'customer' && user?.role !== 'project_leader') {
        const isManager = user?.role === 'manager' || user?.role === 'admin';
        await warmShaped(
          'teams:list',
          async () => {
            const res: any = await api.listTeams(isManager);
            return res?.teams || [];
          },
          { manager: isManager },
        );
      }

      // Low-priority — admin / settings pages. Skip silently for
      // non-admin users (the endpoints return 403 and that's fine).
      // FIX (Jun 01 2026): listAlertRules returns { rules: [...] } but
      // AlertRulesPage reads the cached value as a bare ARRAY (it guards
      // with Array.isArray). Storing the wrapper object made the guard
      // fail and was part of the "rules.map is not a function" family of
      // bugs. Unwrap to the array so the shape matches the reader.
      await warmShaped('alert-rules:list', async () => {
        const res: any = await api.listAlertRules(true);
        return Array.isArray(res) ? res : (res?.rules ?? []);
      });
      // Approvals is an admin-only page (sidebar roles: ['admin']) and
      // /customer/registrations returns 403 for everyone else. Only the
      // warm() error was swallowed before — the request still fired and
      // littered the console with a 403 for every customer/staff login.
      // Gate the prefetch on role so we only call it when it can succeed.
      if (user?.role === 'admin') {
        // EXTRACT THE LIST (May 25 2026): api.getPendingRegistrations returns
        // {registrations: [...]} but ApprovalsPage caches & reads JUST the array.
        // Without this unwrap the cache stored the wrapper object → ApprovalsPage
        // crashed with "registrations.map is not a function" on next visit.
        await warmShaped('approvals:pending', async () => {
          const res: any = await api.getPendingRegistrations();
          const list = Array.isArray(res) ? res : (res?.registrations ?? res?.pending ?? []);
          return Array.isArray(list) ? list : [];
        });
      }

      // Customer dashboard pre-fetch — matches CustomerDashboard's
      // getCached('customer-dashboard:state', { customerCode }) lookup.
      const customerCode = user?.customer_code || null;
      await warmShaped(
        'customer-dashboard:state',
        async () => {
          const [statsData, whData, custData, expiryData, palletData, capData, inboundData, transData] =
            await Promise.all([
              api.getDataStats().catch(() => null),
              api.getWarehouses().catch(() => ({ warehouses: [] })),
              api.getCustomers().catch(() => ({ customers: [] })),
              api.getExpiry().catch(() => null),
              api.getPallets().catch(() => null),
              api.getCapacity().catch(() => null),
              api.getInbound().catch(() => null),
              api.getTransportStats().catch(() => ({ stats: {} })),
            ]);
          return {
            stats: statsData,
            warehouses: (whData as any)?.warehouses || [],
            customers: (custData as any)?.customers || [],
            expiry: expiryData,
            pallets: palletData,
            capacity: capData,
            inbound: inboundData,
            transportStats: (transData as any)?.stats || {},
          };
        },
        { customerCode },
      );
    };

    // Start AFTER the chunks above so the browser isn't doing too much
    // at once on a slow connection. 1.5s gives the page paint cycle room.
    const startDelay = setTimeout(() => { void warmUp(); }, 1500);
    return () => { cancelled = true; clearTimeout(startDelay); };
  }, [isAuthenticated]);

  // ── Keep-alive page cache ──
  //
  // Before: switching tabs unmounted the previous page and mounted the
  // new one from scratch. Every tab change cost a full re-render of the
  // destination page AND a complete round-trip of its API calls (even
  // if the data was 5 seconds old and identical). That's why tab
  // switching felt slow.
  //
  // After: each page mounts exactly ONCE per session, the first time
  // you visit it. Subsequent visits just toggle which page is visible
  // via `display: none`. State persists, network requests don't refire,
  // scroll position is preserved. Switching from Inventory to Inbound
  // and back is now near-instant.
  //
  // Memory cost: ~5–15 MB per kept-alive page on a heavy dashboard. A
  // typical user visits 4–6 pages per session so total overhead stays
  // under 100 MB even on tablet hardware. Pages that haven't been
  // visited yet aren't rendered at all (they're not in the cache).
  //
  // NOTE (hooks-order fix, May 17 2026): these three hooks (useMemo +
  // useState + useEffect) MUST run before the `if (loading)` /
  // `if (!isAuthenticated)` early returns below. Otherwise React's
  // rules-of-hooks lint rule rightly complains that hooks 4+ run only
  // on some renders. The page factory is a pure function of the menu
  // setter (stable) so the empty dep array is correct.
  const PAGE_FACTORY: Record<string, () => React.ReactNode> = React.useMemo(() => ({
    'ai-home':          () => <AIHomePage onNavigate={setActiveMenu} />,
    'dashboard':        () => <DashboardPage />,
    'inventory':        () => <InventoryPage />,
    'inbound':          () => <InboundPage />,
    'outbound':         () => <OutboundPage />,
    'transport':        () => <TransportPage />,
    'bookings':         () => <BookingsPage />,
    'documents':        () => <DocumentsPage />,
    'appointments':     () => <AppointmentsPage />,
    'expiry':           () => <ExpiryPage />,
    'alarms':           () => <AlarmsPage />,
    'alert-rules':      () => <AlertRulesPage />,
    'reports':          () => <ReportsPage />,
    'satisfaction':     () => <SatisfactionPage />,
    'project-leaders':  () => <ProjectLeadersPage />,
    'teams':            () => <TeamsPage />,
    'chat':             () => <ChatPage />,
    'announcements':    () => <AnnouncementsPage />,
    'approvals':        () => <ApprovalsPage />,
    'customer-scope':   () => <CustomerScopePage />,
    'unmapped-bmfirms': () => <UnmappedBMFirmsPage />,
    'settings':         () => <SettingsPage />,
  }), []);

  // Set of pages we've ever mounted. We only ever ADD to this set, so
  // unmounting and re-visiting a page in the same session never refetches.
  const [visited, setVisited] = React.useState<Set<string>>(() => new Set([activeMenu]));
  React.useEffect(() => {
    setVisited(prev => {
      if (prev.has(activeMenu)) return prev;
      const next = new Set(prev);
      next.add(activeMenu);
      return next;
    });
  }, [activeMenu]);

  // ── Early returns — MUST come AFTER every useXxx call above. ──
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // On mobile: no sidebar margin. On tablet: collapsed sidebar. On desktop: normal.
  const effectiveCollapsed = isTablet ? true : sidebarCollapsed;
  const sw = isMobile ? 0 : (effectiveCollapsed ? 56 : 220);

  const renderPage = () => {
    // Render every visited page side-by-side; only the active one is
    // visible. Inactive pages stay mounted so their state + data
    // persist across navigation.
    const slots: React.ReactNode[] = [];
    visited.forEach(key => {
      const factory = PAGE_FACTORY[key];
      if (!factory) return;
      const isActive = key === activeMenu;
      slots.push(
        <div
          key={key}
          style={{
            // `display: none` removes the subtree from layout + paint
            // while preserving React's component instances + their
            // state. Cheaper than conditional rendering for this use
            // case and avoids the "Loading…" flash.
            display: isActive ? 'block' : 'none',
            minHeight: isActive ? '100%' : undefined,
          }}
          aria-hidden={!isActive}
        >
          {factory()}
        </div>
      );
    });
    // Fallback for the AI home if user hasn't visited anything yet.
    if (slots.length === 0) {
      return PAGE_FACTORY['ai-home']();
    }
    return <>{slots}</>;
  };

  const isFullPage = activeMenu === 'ai-home' || activeMenu === 'chat';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Sidebar
        activeMenu={activeMenu}
        onMenuChange={setActiveMenu}
        collapsed={effectiveCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        isMobile={isMobile}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />
      <TopBar
        pageTitle={PAGE_TITLES[activeMenu] || ''}
        sidebarWidth={sw}
        isMobile={isMobile}
        onHamburgerClick={() => setMobileMenuOpen(true)}
      >
        <NotificationCenter onNavigate={setActiveMenu} />
      </TopBar>
      <main
        className="app-main-content"
        style={{
          marginLeft: sw,
          // 8px top gap + 48px topbar = 56px total reserved at the top
          // (matches the sidebar/topbar `top: 8` offset set in Sidebar.tsx
          // and TopBar.tsx). Mobile keeps 48px so the drawer pattern still
          // feels like a native sheet anchored to the very top.
          paddingTop: isMobile ? 48 : 56,
          minHeight: '100vh',
          transition: 'margin-left 0.2s ease',
        }}
      >
        {/* Global drill-down banner — shows the active warehouse/customer
            filter (set by clicking a Dashboard card) and a clear button.
            Visible on every page. (Jun 03 2026) */}
        {gf.active && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            margin: isMobile ? '8px 12px 0' : '10px 28px 0',
            padding: '8px 14px', borderRadius: 10,
            background: 'var(--accent-soft, rgba(99,102,241,0.10))',
            border: '1px solid var(--accent, #6366f1)',
            fontSize: 13,
          }}>
            <span style={{ fontWeight: 700, color: 'var(--accent, #6366f1)', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>● Drill-down</span>
            <span style={{ color: 'var(--text-secondary)' }}>Showing only</span>
            {gf.warehouse && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700, color: 'var(--text)', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 6, padding: '3px 8px' }}>
                🏢 {gf.warehouse}
                <button onClick={() => gf.setWarehouse(null)} title="Clear warehouse" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, lineHeight: 1, padding: 0 }}>✕</button>
              </span>
            )}
            {gf.customer && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700, color: 'var(--text)', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 6, padding: '3px 8px' }}>
                👤 {gf.customer}
                <button onClick={() => gf.setCustomer(null)} title="Clear customer" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, lineHeight: 1, padding: 0 }}>✕</button>
              </span>
            )}
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>across every module</span>
            <button onClick={() => gf.clear()} style={{ marginLeft: 'auto', border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: 'var(--text)', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Clear all</button>
          </div>
        )}
        <div
          // Key the page subtree on the active filter so changing the drill-down
          // remounts the current page → it refetches with the new warehouse/
          // customer (the API client injects the params). Lets every module
          // follow one selection without per-page wiring.
          key={`gf:${gf.warehouse || ''}|${gf.customer || ''}`}
          style={{
            // FIX 2026-06-07: give the chat page a gap from the sidebar (it was
            // flush against the menu, padding:0). Chat keeps a small inset on
            // desktop so it reads as a separate panel; mobile stays full-bleed.
            padding: activeMenu === 'chat'
              ? (isMobile ? 0 : '14px 16px')
              : (isFullPage ? 0 : (isMobile ? '16px 12px' : '24px 28px')),
            height: isFullPage
              ? (isMobile ? 'calc(100vh - 48px)' : 'calc(100vh - 56px)')
              : undefined,
          }}>
          <ErrorBoundary>
            <Suspense fallback={<PageSuspenseFallback />}>
              {renderPage()}
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>

      {/* Global Search Overlay (Ctrl+K) */}
      <GlobalSearch
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        onNavigate={(page) => { setActiveMenu(page); setSearchOpen(false); }}
      />
    </div>
  );
};

function App() {
  // ── L2 cache hydration ──
  // Pull every disk-cached GET response from IndexedDB into the L1 RAM
  // cache so the first paint of every page has data instead of a
  // Loading spinner. Fire-and-forget — we do NOT await before render
  // so hydration races the first network request; whichever resolves
  // first wins (cache hits paint instantly, misses fall through to
  // network as normal). Idempotent — safe to call repeatedly.
  useEffect(() => { void hydrateApiCache(); }, []);

  return (
    <ThemeProvider>
    <AuthProvider>
      <GlobalFilterProvider>
      <BrowserRouter>
        {/* Top progress bar — fires on every API call and on Suspense
            chunk-loads. Driven by ref-counted startProgress/stopProgress
            in TopProgressBar.tsx, hooked into axios interceptors below. */}
        <TopProgressBar />
        {/* Global toast renderer — listens to addToast() calls from anywhere
            in the app and shows the toast in the top-right corner. Mounted
            once at the root so feedback works on every page, including login. */}
        <NotificationToast />
        <Routes>
          <Route path="/login" element={<LoginWrapper />} />
          <Route path="/*" element={<ProtectedLayout />} />
        </Routes>
      </BrowserRouter>
      </GlobalFilterProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

const LoginWrapper: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <LoginPage />;
};

export default App;
